<div class="panels">
  <section class="table-card panel wallet">
    <h3><mat-icon>account_balance_wallet</mat-icon> Hot wallet</h3>
    @if (walletError) {
    <p class="error">{{ walletError }}</p>
    } @else if (!wallet) {
    <mat-spinner diameter="24"></mat-spinner>
    } @else if (!wallet.enabled) {
    <p class="muted">
      On-chain payouts are off: this server has no hot wallet key. Create a new wallet used only for payouts,
      put its private key in <code>CRYPTO_PAYOUT_PRIVATE_KEY</code> on the server and restart it. Then send it a
      float of USDT (BEP-20) and a little BNB for gas from your main wallet.
    </p>
    } @else {
    <div class="address" dir="ltr">
      <span class="mono">{{ wallet.address }}</span>
      <button mat-icon-button type="button" matTooltip="Copy address" (click)="copyAddress()"><mat-icon>content_copy</mat-icon></button>
    </div>
    <p class="sub">{{ wallet.network }} · top it up from your main wallet with USDT (BEP-20) and some BNB for gas.</p>
    @if (wallet.balance_error) {
    <p class="error">Could not read the chain: {{ wallet.balance_error }}</p>
    } @else {
    <div class="stats">
      <div class="stat"><span class="label">USDT</span><span class="value">{{ wallet.token_balance }}</span></div>
      <div class="stat"><span class="label">BNB (gas)</span><span class="value">{{ wallet.gas_balance }}</span></div>
      <div class="stat"><span class="label">In flight</span><span class="value">{{ wallet.open_count }}</span></div>
    </div>
    }
    <div class="limit">
      <span class="label">Sent in the last 24 h</span>
      <span>{{ wallet.sent_last_24h }} of {{ wallet.daily_limit }} USDT</span>
      <div class="meter"><div [style.width.%]="usedPercent"></div></div>
    </div>
    }
  </section>

  <section class="table-card panel settings">
    <h3><mat-icon>tune</mat-icon> Limits</h3>
    <mat-slide-toggle [(ngModel)]="autoPay" [disabled]="!canEdit">
      Auto-pay new requests
    </mat-slide-toggle>
    <p class="sub">When on, a supplier's USDT request is sent as soon as they make it, if its payout is at most the
      auto-pay max. Larger ones wait for you.</p>
    <div class="row">
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Auto-pay max (USDT)</mat-label>
        <input matInput [(ngModel)]="autoPayMax" inputmode="decimal" dir="ltr" [disabled]="!canEdit">
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>24-hour limit (USDT)</mat-label>
        <input matInput [(ngModel)]="dailyLimit" inputmode="decimal" dir="ltr" [disabled]="!canEdit">
      </mat-form-field>
    </div>
    <p class="sub">The 24-hour limit caps everything sent, auto or by hand. 0 stops all on-chain payouts.</p>
    <mat-form-field appearance="outline" subscriptSizing="dynamic">
      <mat-label>Record payments as paid from</mat-label>
      <mat-select [(ngModel)]="sourceAccountId" [disabled]="!canEdit">
        @for (s of sources; track s.id) {
        <mat-option [value]="s.id">{{ s.name }} ({{ s.hesabfa_code }})</mat-option>
        }
      </mat-select>
      @if (sources.length === 0) {
      <mat-hint>Add the hot wallet under Paid-from accounts (kind Crypto wallet) first.</mat-hint>
      }
    </mat-form-field>
    @if (canEdit) {
    <div class="actions">
      <button mat-flat-button color="primary" type="button" [disabled]="savingSettings" (click)="saveSettings()">Save</button>
    </div>
    }
  </section>
</div>

<div class="list-toolbar">
  <mat-form-field appearance="outline" subscriptSizing="dynamic" class="f-status">
    <mat-label>Status</mat-label>
    <mat-select [(ngModel)]="status" (selectionChange)="onStatus()">
      <mat-option value="">All</mat-option>
      @for (s of statuses; track s) { <mat-option [value]="s">{{ s }}</mat-option> }
    </mat-select>
  </mat-form-field>
</div>

<div class="table-card">
@if (loading && payouts.length === 0) {
<div class="table-state"><mat-spinner diameter="28"></mat-spinner></div>
} @else if (error) {
<div class="table-state error">{{ error }}</div>
} @else if (payouts.length === 0) {
<div class="table-state">No on-chain payouts yet.</div>
} @else {
<div class="table-scroll">
  <table mat-table [dataSource]="payouts">
    <ng-container matColumnDef="created">
      <th mat-header-cell *matHeaderCellDef>Queued</th>
      <td mat-cell *matCellDef="let p">{{ p.created_at | date:'short' }}
        <div class="sub">{{ p.auto_paid ? 'auto-pay' : 'by an admin' }}</div></td>
    </ng-container>
    <ng-container matColumnDef="supplier">
      <th mat-header-cell *matHeaderCellDef>Supplier</th>
      <td mat-cell *matCellDef="let p"><a [routerLink]="['/payments/requests', p.request_id]">{{ p.supplier_name }}</a>
        <div class="sub mono" dir="ltr">{{ p.to_address }}</div></td>
    </ng-container>
    <ng-container matColumnDef="amount">
      <th mat-header-cell *matHeaderCellDef>Amount</th>
      <td mat-cell *matCellDef="let p" class="num"><span class="strong">{{ p.amount }} USDT</span>
        @if (p.request_amount) { <div class="sub">{{ p.request_amount | number }} Toman</div> }</td>
    </ng-container>
    <ng-container matColumnDef="status">
      <th mat-header-cell *matHeaderCellDef>Status</th>
      <td mat-cell *matCellDef="let p">
        <span class="chip" [ngClass]="statusClass(p.status)">{{ label(p) }}</span>
        @if (p.error) { <div class="sub err">{{ p.error }}</div> }
      </td>
    </ng-container>
    <ng-container matColumnDef="tx">
      <th mat-header-cell *matHeaderCellDef>Transaction</th>
      <td mat-cell *matCellDef="let p" dir="ltr">
        @if (p.explorer_url) {
        <a class="mono" [href]="p.explorer_url" target="_blank" rel="noopener">{{ p.tx_hash | slice:0:10 }}…{{ p.tx_hash | slice:-6 }}</a>
        } @else { <span class="muted">—</span> }
        @if (p.gas_fee) { <div class="sub">gas {{ p.gas_fee }} BNB</div> }
      </td>
    </ng-container>
    <ng-container matColumnDef="actions">
      <th mat-header-cell *matHeaderCellDef></th>
      <td mat-cell *matCellDef="let p" class="actions">
        @if (canEdit && p.status === 'Queued') {
        <button mat-button type="button" color="warn" (click)="cancel(p)">Cancel</button>
        }
      </td>
    </ng-container>
    <tr mat-header-row *matHeaderRowDef="columns"></tr>
    <tr mat-row *matRowDef="let row; columns: columns;"></tr>
  </table>
</div>
<mat-paginator [length]="total" [pageIndex]="pageIndex" [pageSize]="pageSize" [pageSizeOptions]="[20, 50, 100]"
  (page)="onPage($event)"></mat-paginator>
}
</div>
